import { zodResolver } from "@hookform/resolvers/zod";
import { IconCircleX, IconUserPlus } from "@tabler/icons-react";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Button, Col, Form, InputGroup, Offcanvas, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import TagsInput from "react-tagsinput";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { z } from "zod";
import { toast } from "sonner";
import { getCities, getCountries, getStates } from "../Utils/hooks/location";
import { GetRequest } from "../Utils/hooks/ApiHandler";
import { usePostRequest } from "../Utils/hooks/QueryHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { checkDuplicate, customStyles, useDebounce } from "../Utils/helpers/commonhelper";
import validator from "validator";
import { parsePhoneNumber } from "libphonenumber-js/min";
import {
  DateField,
  MultipleOpt,
  PhoneNumberInput,
  SelectOpt,
  Text,
  Time,
} from "./FieldComp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { CreateCompany } from "./CreateCompany";
import { UsersPopup } from "./UsersPopup";
import { ContactFormCommonOffcanvas } from "../Components/education/Contacts/ContactModule/ContactFormCommonOffcanvas";

// Placeholder for baseURL, replace with your actual constant
const baseURL = "https://your-base-url.com/";

const optionalUrl = (label) =>
  z
    .string()
    .trim()
    .optional()
    .refine(
      (val) => !val || !val.includes(" "),
      `${label} URL cannot contain spaces`
    )
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      `Invalid ${label} URL`
    );

const contactSchema = z.object({
  salutation: z.string().default("Mr."),
  first_name: z.string().min(1, "Required"),
  last_name: z.string(),
  owner_id: z.string().optional(),

  visible_to: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        type: z.enum(["user", "team", "all"]),
      })
    )
    .nonempty("Required !"),

  report_to: z.string().optional(),
  address: z.string().optional(),
  countryId: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  zipcode: z.string().optional(),

  facebook: optionalUrl("Facebook"),
  twitter: optionalUrl("Twitter"),
  instagram: optionalUrl("Instagram"),

  linkedin: optionalUrl("Linkedin"),

  company_id: z.string().min(1, "Required !"),
  department: z.string().optional(),
  designation: z.string().optional(),
  lead_source_id: z.string().min(1, "Required !"),
  note: z.string().optional(),
});


export const EditContactOffcanvas = ({
  show,
  setShow,
  contact_data,
  isOwnerSame,
  refetch,
  allContacts,
  temData,
  showContactDetailsCanvas,
  setShowContactDetailsCanvas,

  setActiveCanvas,

}) => {
  // customer data from sesssion storage
  const [customer, setCustomer] = useLocalStorageState("customer");
  // token obj
  const headers = {
    Authorization: customer?.token.authorization,
    Authentication: customer?.token.authentication,
  };

  // permission setup for conntacts
  const permission = customer.customer_details?.permission
  const contactPermssion = permission?.filter(c => c.module_name === "contact")
  const user_type = Number(customer.customer_details?.user_type)
  // company permissions while creating contact
  const companyPermission = permission?.filter(c => c.module_name === "company")
  const companyGrants = {
    isCreate: user_type !== 1 ? Boolean(Number(companyPermission?.find(c => c.feature_name === "company")?.create)) : true,
  }
  // for lead source permission
  const leadSourcePermission = permission?.filter(c => c.module_name === "setting")
  const masterGrant = {
    isCreate: user_type !== 1 ? Boolean(Number(leadSourcePermission?.find(c => c.feature_name === "master")?.create)) : true,
  }

  // function to set the dynamic fields for the dynamic fields
  const generateDynamicSchema = (fields) => {
    const schemaFields = {};
    fields.forEach((field) => {
      const fieldId = field.id;
      switch (field.label_type) {
        case "text":
        case "largeText":
        case "address":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;
          break;
        case "numerical":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string({ required_error: `${field.label_name} is required` })
              : z.string().optional();
          break;
        case "monetary":
          schemaFields[fieldId] = field.is_required === "yes"
            ? z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
              z.number({
                required_error: `${field.label_name} is required`,
                invalid_type_error: "Please enter a valid number"
              })
                .min(0.01, "Amount must be positive")
            )
            : z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
              z.number().min(0, "Amount cannot be negative").optional().nullable()
            );
          break;
        case "single-option":
        case "user":
        case "person":
        case "organization":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;
        case "date":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;
        case "date range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z
                .object({
                  start: z.string().min(1, "Start date is required"),
                  end: z.string().min(1, "End date is required"),
                })
                .refine((data) => data.start <= data.end, {
                  message: "End date must be after start date",
                })
              : z
                .object({
                  start: z.string().optional(),
                  end: z.string().optional(),
                })
                .optional();
          break;
        case "time":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;
        case "time-range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.object({
                start: z.string().min(1, "Start time is required"),
                end: z.string().min(1, "End time is required"),
              })
              : z
                .object({
                  start: z.string().optional(),
                  end: z.string().optional(),
                })
                .optional();
          break;
        case "phone":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, "Phone number is required")
              : z.string().optional();
          break;
        case "multiple-option": // Added multiple-option schema
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.array(z.string()).min(1, `${field.label_name} is required`)
              : z.array(z.string()).optional();
          break;
        default:
          schemaFields[fieldId] = z.any().optional();
      }
    });
    return z.object(schemaFields);
  };

  const parseVisibleToData = (rawData) => {
    if (!rawData) return [];

    try {
      let parsed = rawData;

      if (typeof parsed === "string") {
        if (parsed.toLowerCase() === "all") {
          return [{ id: "0", label: "All", type: "all" }];
        }
        parsed = JSON.parse(parsed);
      }

      const dataArray = Array.isArray(parsed) ? parsed : [parsed];

      return dataArray.map((item) => ({
        id: String(item.id || item.value || ""),
        label: item.label || item.name || "",
        type: item.type || "user",
      }));
    } catch (err) {
      console.error("Error parsing visible_to:", err, rawData);
      return [];
    }
  };

  // 2. Initialize visible_to before useForm
  const initialVisibleTo = useMemo(() => {
    return parseVisibleToData(contact_data?.visible_to);
  }, [contact_data?.visible_to]);

  // groped state to hold the dynamic group data
  const [grouped, setGrouped] = useState([]);
  console.log(grouped)
  // ungroped state to hold the dynamic group data
  const [ungrouped, setUnGrouped] = useState([]);
  // state to store all dynamic fields
  const [dynamicFields, setDynamicFields] = useState([]);
  console.log(dynamicFields)
  // Memoize the combined schema to avoid unnecessary recalculations
  const finalSchema = useMemo(() => {
    if (dynamicFields.length === 0) {
      return contactSchema;
    }

    // Combine grouped and ungrouped fields
    const allDynamicFields = [
      ...(grouped?.flatMap((g) => g.groupFields || []) || []),
      ...(ungrouped || []),
    ];

    if (allDynamicFields.length === 0) {
      return contactSchema;
    }

    const dynamicSchema = generateDynamicSchema(allDynamicFields);

    // Merge static and dynamic schemas
    return contactSchema.merge(dynamicSchema);
  }, [grouped, ungrouped, dynamicFields]);



  // sirf IDs extract
  // const visibleToIds = parsedVisibleTo.map(item => item.id);
  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    resolver: zodResolver(finalSchema),
    defaultValues: {
      ...contact_data,
      visible_to: initialVisibleTo,
      country: contact_data?.country || "99", // Default to India if no country
      state: contact_data?.state,
      city: contact_data?.city,
    },
  });

  // Update the visible_to state initialization
  const [visible_to, setVisibleTo] = useState(initialVisibleTo);


  // image state
  const [image, setImage] = useState(
    contact_data?.contact_image !== "" ? contact_data?.contact_image : null
  );
  const navigate = useNavigate();

  // required fields state
  const [showRequired, setShowRequired] = useState(true);
  // Helper function to check if field should be shown
  const shouldShowField = (field) => {
    if (!showRequired) return true; // Show all fields when toggle is off

    // When toggle is on, show only fields that are required OR important
    return field.is_required === "yes" || field.is_important === "yes";
  };

  // emails
  const [emails, setEmails] = useState(
    contact_data?.user_email
      ? JSON.parse(contact_data?.user_email)
      : [{ email: "", email_type: "Office" }]
  );

  const phoneOptions = [
    { value: "Mobile", label: "Mobile" },
    { value: "Work", label: "Work" },
    { value: "Home", label: "Home" },
    { value: "Personal", label: "Personal" },
  ]
  const emailOptions = [
    { value: "Office", label: "Office" },
    { value: "Personal", label: "Personal" },
  ]

  // contacts
  const [contacts, setContacts] = useState(
    contact_data?.user_phone
      ? JSON.parse(contact_data?.user_phone)
      : [{ phone: "", phone_type: "Mobile" }]
  );
  // states for empty emails and contact
  const [emptyEmailIndices, setEmptyEmailIndices] = useState([]);
  const [emptyContactIndices, setEmptyContactIndices] = useState([]);
  // refs for scrolling
  const emailSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const tagSectionRef = useRef(null);
  // duplicacy with respect to the primary email and contact
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null);
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null);
  const debouncedEmail = useDebounce(emails[0]?.email || "", 500);
  const debouncedPhone = useDebounce(contacts[0]?.phone || "", 500);
  // timezone state
  const [selectedTimezone, setSelectedTimezone] = useState(
    contact_data?.timezone ? JSON.parse(contact_data?.timezone) : {}
  );
  // country state
  const [country, setCountries] = useState([
    {
      countryName: "",
      countryId: contact_data?.country,
    },
  ]);
  // states state
  const [states, setStates] = useState([
    {
      stateName: "",
      stateCode: contact_data?.state,
    },
  ]);
  // city state
  const [city, setCity] = useState([
    {
      cityName: "",
      cityCode: contact_data?.city,
    },
  ]);
  // decision state
  const [decision, setDecision] = useState(
    contact_data?.decision_maker === "Yes" ? true : false
  );
  // tags state
  const [tags, setTags] = useState(
    contact_data?.tag ? JSON.parse(contact_data?.tag) : []
  );
  // lead source state
  const [lead_source, setLeadSource] = useState([
    {
      source_title: "",
      lead_source_id: contact_data?.lead_source_id,
    },
  ]);
  // lead owner state
  const [leadOwner, setLeadOwner] = useState([
    {
      leadName: "",
      leadId: contact_data?.owner_id,
    },
  ]);
  const [teams, setTeams] = useState([
    {
      teamName: "",
      teamId: "",
    },
  ]);
  // company canvas state
  const [companyShow, setCompanyShow] = useState(false)
  // company state
  const [company, setCompany] = useState([
    {
      company_name: "",
      company_id: contact_data?.company_id,
    },
  ]);
  // autoselect state
  const [recentCompany, setRecentCompanyId] = useState({
    recentCompanyId: "",
    recentCompanyName: "",
  })
  const [recentSource, setRecentSource] = useState({
    recentSourceId: "",
    recentSourceName: "",
  })
  // state to open the lead source form
  const [sourceShow, setSourceShow] = useState(false)

  // company section ref
  const companyRef = useRef(null);

  const visibleToRef = useRef(null);
  // lead source section ref
  const leadSourceRef = useRef(null)

  // add email field handler
  const handleAddEmail = () => {
    setEmails([
      ...emails,
      {
        email: "",
        email_type: "Office",
      },
    ]);
  };

  // add contact field handler
  const handleAddContact = () => {
    setContacts([
      ...contacts,
      {
        phone: "",
        phone_type: "Mobile",
      },
    ]);
  };

  // delete email handler
  const handleDeleteEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    // Also remove from emptyEmailIndices if it was there
    setEmptyEmailIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  // delete contact handler
  const handleDeleteContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
    // Also remove from emptyContactIndices if it was there
    setEmptyContactIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  // adding email inputs to email state array
  const handleEmailChange = (field, value, index) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setEmails(newEmails);
    // If the email field is no longer empty, remove its index from emptyEmailIndices
    if (field === "email" && value.trim() !== "") {
      setEmptyEmailIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  // adding contact inputs to contact state array
  const handleContactChange = (field, value, index, details) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value, ...details };
    setContacts(newContacts);
    // If the phone field is no longer empty, remove its index from emptyContactIndices
    if (field === "phone" && value.trim() !== "") {
      setEmptyContactIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  const validatePhoneNumber = (rawNumber, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        String(rawNumber),
        String(countryCode?.toUpperCase())
      );
      const valid = phoneNumber?.isValid() || false;
      return valid;
    } catch (error) {
      return false;
    }
  };

  // tags handler
  const handleTagChange = (newTags) => {
    const uniqueTags = [];

    newTags.forEach((tag) => {
      const lowerTag = tag.toLowerCase();

      const isDuplicate = uniqueTags.some((t) => t.toLowerCase() === lowerTag);
      if (!isDuplicate) {
        uniqueTags.push(tag);
      }
    });

    setTags(uniqueTags);
  };

  // useeffect for country
  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();
        if (!data) return;
        // Update state once with all country data
        setCountries(
          data.map((country) => ({
            countryName: country.name,
            countryId: country.id,
          }))
        );
      } catch (error) {
        toast.error("Error Fetching Countries");
        return;
      }
    })();
  }, []);

  // useeffect for state
  const countryId = watch("country");
  const stateId = watch("state");
  useEffect(() => {
    (async () => {
      try {
        if (dirtyFields.countryId) {
          setValue("state", "");
          setValue("city", ""); // Also clear city when country changes
        }
        const data = await getStates(countryId);
        if (!data || data === undefined) {
          setStates([]);
          setCity([]); // Clear cities when no states
          setValue("state", "");
          setValue("city", "");
          return;
        }
        if (stateId === " " || stateId === "0") setValue("state", "");
        // Update state once with all country data
        setStates(
          data.map((state) => ({
            stateName: state.state_name,
            stateCode: state.id,
          }))
        );
        // Clear cities when states change
        if (dirtyFields?.state) {
          setCity([]);
          setValue("city", "");
        }
      } catch (error) {
        toast.error("Error Fetching State");
        return;
      }
    })();
  }, [countryId]);

  // city useeffect
  useEffect(() => {
    (async () => {
      try {
        if (!stateId || stateId === "0" || stateId === "") {
          setCity([]);
          setValue("city", ""); // Clear city selection
          return;
        }
        // Clear city when state changes
        if (dirtyFields.state) {
          setValue("city", "");
        }
        const data = await getCities(stateId);
        if (!data || data === undefined) {
          setCity([]);
          setValue("city", "");
          return;
        }
        // FIXED: Replace the cities array completely
        setCity(
          data.map((city) => ({
            cityName: city.city_name,
            cityCode: city.id,
          }))
        );
      } catch (error) {
        toast.error("Error Fetching Cities");
        return;
      }
    })();
  }, [stateId]);

  // lead source api fetch
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `masters/getAllLead_source/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
      );
      if (!res) return;
      setLeadSource(
        res.data.map((lead) => ({
          source_title: lead.source_title,
          lead_source_id: lead.id,
        }))
      );
    })();
  }, []);

  // useeffect for fetching users for lead_owners, visible_to, report_to
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
      );
      if (!res) return;
      if (res.err) {
        toast.error("Error Fetching Data");
      }
      // Update state once with all new leads
      setLeadOwner(
        res.data.map((lead) => ({
          leadName: lead.full_name,
          leadId: lead.id,
        }))
      );
    })();
  }, []);

  // useEffetct fot fetching teams data

  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `/users/users/fetchAllTeams/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
      );
      if (res.err) {
        toast.error("Error Fetching Data");
      }
      const teamsData =
        res.data &&
        res.data.map((team) => ({
          teamName: team.team_name,
          teamId: team.id,
        }));
      setTeams(teamsData);
      // if (teams && teams.length > 0) {
      //   setValue("owner_id", customer.customer_details?.id);
      // }
    })();
  }, []);

  // useeffect for companies
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `company/fetchCompanies/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
      );
      if (!res) return;
      if (res.success === false) {
        toast.error(res.msg);
        return;
      }
      setCompany(
        res.data.map((c) => ({
          company_name: c.company_name,
          company_id: c.id,
        }))
      );
    })();
  }, [setCompany]);

  // Effect for email duplication check
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail) {
        setDuplicateEmailMessage(null);
        const isEmailExisting = await checkDuplicate({
          col: "email",
          customer_id: customer.customer_details?.customer_id, // Hardcoded as per example
          validating_val: debouncedEmail,
          contact_id: "", // Hardcoded as per example
          checkFor: "contacts",
          existing_val: contact_data.email || "",
        });
        if (isEmailExisting) {
          setDuplicateEmailMessage("Email already exists!");
        }
      } else {
        setDuplicateEmailMessage(null);
      }
    };
    checkEmail();
  }, [debouncedEmail]);

  // Effect for phone duplication check
  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone) {
        setDuplicatePhoneMessage(null);
        const isPhoneExisting = await checkDuplicate({
          col: "phone",
          customer_id: customer.customer_details?.customer_id,
          validating_val: debouncedPhone,
          contact_id: "",
          checkFor: "contacts",
          existing_val: contact_data.phone || "",
        });
        if (isPhoneExisting) {
          setDuplicatePhoneMessage("Phone number already exists!");
        }
      } else {
        setDuplicatePhoneMessage(null);
      }
    };
    checkPhone();
  }, [debouncedPhone]);

  // fetching dynamic fields for the contact form

  // Function to safely parse the visible_to string
  const parseVisibleTo = (val) => {
    try {
      if (typeof val === "string") return JSON.parse(val);
      return val || [];
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    const initDynamicFields = async () => {
      try {
        const res = await GetRequest(
          `contacts/fetchCustomFields/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        );
        console.log(res)
        if (res.status === "success") {
          const allFields = [
            ...(res.data?.grouped?.flatMap((g) => g.groupFields || []) || []),
            ...(res.data?.ungrouped || []),
          ];

          setGrouped(res.data.grouped || []);
          setUnGrouped(res.data.ungrouped || []);
          setDynamicFields(allFields);

          const dynamicFieldValues = {};
          if (contact_data?.custom_fields) {
            contact_data?.custom_fields.forEach((cf) => {
              try {
                dynamicFieldValues[cf.field_id] = JSON.parse(cf.value);
              } catch {
                dynamicFieldValues[cf.field_id] = cf.value;
              }
            });
          }
          // console.log(contact_data, dynamicFieldValues, "dynamicFieldValues")

          // ✅ Set visible_to properly
          const parsedVisibleTo = parseVisibleToData(contact_data?.visible_to);
          setValue("visible_to", parsedVisibleTo);
          setVisibleTo(parsedVisibleTo);


          reset({
            ...contact_data,
            owner_id: contact_data?.owner_id,
            visible_to: parsedVisibleTo,
            company_id: contact_data?.company_id,
            lead_source_id: contact_data?.lead_source_id,
            state: contact_data?.state,
            city: contact_data?.city,
            report_to: contact_data?.report_to || "",
            ...dynamicFieldValues,
          })

        }
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    initDynamicFields();
  }, [contact_data.id, customer]);

  const handleClose = () => {
    // Reset React Hook Form fields
    reset({
      ...contact_data,
      // visible_to: contact_data.owner_id,
      visible_to: contact_data.parsedVisibleTo || [],
      report_to: contact_data.report_to,
      company_id: contact_data.company_id,
      lead_source_id: contact_data.lead_source_id,
      // state: contact_data.state,
      // city: contact_data.city,
    });
    // setVisibleTo(
    //   contact_data.parsedVisibleTo?.map((item) => item.id)
    // );
    setVisibleTo(contact_data.parsedVisibleTo || []); // Ensure array
    setEmails(JSON.parse(contact_data.user_email));
    setContacts(JSON.parse(contact_data.user_phone));
    setSelectedTimezone(
      contact_data.timezone
        ? JSON.parse(contact_data.timezone)
        : {}
    );
    setTags(
      contact_data.tag ? JSON.parse(contact_data.tag) : []
    );
    setDecision(contact_data.decision_maker === "Yes");
    setImage(
      contact_data.contact_image !== ""
        ? contact_data.contact_image
        : ""
    );
    setEmptyContactIndices([]);
    setEmptyEmailIndices([]);
    setShow();
    setActiveCanvas && setActiveCanvas(null)
  }

  // react query handler for submitting data
  const { mutate, isLoading } = usePostRequest(
    "/contacts/updateContact",
    true,
    { headers },
    { customer, setCustomer, rf: headers.Authentication, navigate }
  );

  // final submit handler
  const handleFormSubmit = async (formData) => {
    // if (!isValid) return;
    // Checking empty email states and contact states before form submitting
    const newEmptyEmailIndices = emails.reduce((acc, email, index) => {
      if (!email.email || email.email.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);
    const newEmptyContactIndices = contacts.reduce((acc, contact, index) => {
      if (!contact.phone || contact.phone.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);
    setEmptyEmailIndices(newEmptyEmailIndices);
    setEmptyContactIndices(newEmptyContactIndices);
    // scrolling if the email or contact input is empty
    if (newEmptyEmailIndices.length > 0 || newEmptyContactIndices.length > 0) {
      if (newEmptyEmailIndices.length > 0) {
        emailSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (newEmptyContactIndices.length > 0) {
        contactSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    const custom_fields = [];
    const staticFieldsPayload = { ...formData }; // Start with all form data
    dynamicFields.forEach((field) => {
      const fieldName = field.id;
      if (fieldName && formData.hasOwnProperty(fieldName)) {
        let formattedValue = formData[fieldName];
        let isRangeField = false; // Initialize flag for range property

        // Handle complex types for custom_fields payload
        if (
          field.label_type === "date range" ||
          field.label_type === "time-range"
        ) {
          isRangeField = true; // Set flag for range fields
          // Ensure both start and end are present before sending
          if (
            !formattedValue ||
            (!formattedValue.start && !formattedValue.end)
          ) {
            formattedValue = { start: "", end: "" }; // Send empty object if range is incomplete/empty
          }
        } else if (field.label_type === "multiple-option") {
          // Ensure it's an array
          if (!Array.isArray(formattedValue)) {
            formattedValue = []; // Send empty array if not an array
          }
        }

        const customFieldEntry = {
          field_id: field.id.toString(), // Ensure ID is a string
          value: formattedValue || "",
          range: isRangeField ? true : false,
        };
        custom_fields.push(customFieldEntry);

        // Remove dynamic field from static payload to avoid duplication
        delete staticFieldsPayload[fieldName];
      }
    });
    const dataToSend = {
      ...formData,
      contact_id: contact_data.id,
      country: parseInt(formData.country),
      state: parseInt(formData.state || "0"),
      city: parseInt(formData.city || "0"),
      customer_id: customer.customer_details.customer_id,
      decision_maker: !decision ? "No" : "Yes",
      email: emails[0].email,
      phone: contacts[0].phone,
      user_email: emails,
      user_phone: contacts,
      tag: tags.length < 1 ? [] : tags,
      timezone: selectedTimezone,
      contact_image: image && typeof image === "object" ? image[0] : null,
      old_contact_image: contact_data.contact_image,
      // visible_to: formData.owner_id,
      report_to: formData.report_to,
    };

    const finalPayload = {
      user_login_id: customer.customer_details?.id,
      user_id: customer.customer_details?.id,
      custom_fields,
      ...dataToSend,
      visible_to: visible_to
        ? visible_to?.map((opt) => ({
          id: opt.id,
          label: opt.label,
          type: opt.type,
        }))
        : temData?.map((opt) => ({
          id: opt.id,
          label: opt.label,
          type: opt.type,
        })),
    };

    mutate(finalPayload, {
      onSuccess: (data) => {
        if (data.status === "fail") {
          toast.error(data.message);
          return;
        }
        refetch && refetch();
        setShow && setShow();
        toast.success(data.message);
        setShowContactDetailsCanvas && setShowContactDetailsCanvas(false);
        return;
      },
    });
  };

  return (
    <div className="d-flex w-100">
      <ContactFormCommonOffcanvas
        formTitle={"Edit Contact"}
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        showRequired={showRequired}
        setShowRequired={setShowRequired}

        // handler from react hook form
        handleSubmit={handleSubmit}
        register={register}
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}

        // handler for actual form submit
        handleFormSubmit={handleFormSubmit}
        duplicateEmailMessage={duplicateEmailMessage}
        duplicatePhoneMessage={duplicatePhoneMessage}

        emails={emails}
        handleAddContact={handleAddContact}
        handleAddEmail={handleAddEmail}
        handleDeleteContact={handleDeleteContact}
        handleDeleteEmail={handleDeleteEmail}
        handleEmailChange={handleEmailChange}
        handleContactChange={handleContactChange}

        phoneOptions={phoneOptions}
        emailOptions={emailOptions}
        emptyEmailIndices={emptyEmailIndices}
        emptyContactIndices={emptyContactIndices}
        emailSectionRef={emailSectionRef}
        visibleToRef={visibleToRef}
        leadSourceRef={leadSourceRef}
        contactSectionRef={contactSectionRef}

        company={company}
        contacts={contacts}
        leadOwner={leadOwner}
        country={country}
        states={states}
        city={city}
        teams={teams}
        ungrouped={ungrouped}
        grouped={grouped}
        lead_source={lead_source}

        validatePhoneNumber={validatePhoneNumber}
        companyShow={companyShow}
        setCompanyShow={setCompanyShow}
        decision={decision}
        setDecision={setDecision}
        selectedTimezone={selectedTimezone}
        setSelectedTimezone={setSelectedTimezone}
        sourceShow={sourceShow}
        setSourceShow={setSourceShow}

        tags={tags}
        shouldShowField={shouldShowField}
        allContacts={allContacts}
        masterGrant={masterGrant}
        companyGrants={companyGrants}

        handleTagChange={handleTagChange}
      />
      <CreateCompany
        show={companyShow}
        setShow={setCompanyShow}
        contactsModule={true}
        setRecentCompanyId={setRecentCompanyId}
        setContactShow={() => setShow(true)}
      ></CreateCompany>
      <UsersPopup
        setRecentSourceId={setRecentSource}
        sourceShow={sourceShow}
        setSourceShow={setSourceShow}
      />
    </div>
  );
};